// The ONE place tax-invoice numbers come from (audit DUP-23, owner 26 Sep 2026).
//
// The ALWFQ- series is typed by hand on every page (Parking / Salik / Traffic / Corporate / Sales invoice /
// Fuel / Mileage / Monthly Calculator) or suggested from the browser's localStorage (Branch charges). Every
// page now gets its numbers through this service:
//
//   invoiceCounterOn()                 false today: numbering is exactly as the pages always did it
//   issueInvoiceNumbers(count, opts)   the numbers of the invoices being made
//   suggestNextInvoiceNumber()         the number to prefill (null while the counter is off)
//
// Owner, 26 Sep 2026: invoice numbers are not used today - the counter is deferred and OFF. With
// INVOICE_NUMBER_SOURCE = 'typed' nothing reads or writes Firestore: the typed reference is formatted as each
// page always did (ALWFQ-<typed>; several invoices: <n>, <n+1>... or <typed>_1, _2...). Turning the counter on
// later is this one constant ('counter'): one Firestore doc settings/invoice_counter {last} moved on in a
// transaction, a number at or below `last` refused (already issued) unless reprinted. Invoices straight from
// Ejar with Ejar's numbers would be a third source here - no page changes.
//
// With the new store (USE_CONTRACTS_V2, owner 27 Sep 2026: "we will not rely on Firebase anymore") the counter is
// the engine's (POST /api/office/invoice-numbers, GET .../next): numbers drawn from one database sequence and saved
// in the same transaction - never twice, no gap; a typed start may only confirm the next free number (a jump ahead
// is refused 'not-next'). Its refusals come back as the InvoiceNumberError of the same code. Whether the counter
// numbers the invoices at all is still INVOICE_NUMBER_SOURCE below (off today).
import { doc, getDoc, runTransaction } from 'firebase/firestore';
import { db } from '../firebase';
import { USE_CONTRACTS_V2 } from './contractsV2';
import { finesService, officeError } from './finesService';

export const INVOICE_PREFIX = 'ALWFQ-';
export const INVOICE_NUMBER_SOURCE = 'typed';         // 'typed' (today) | 'counter' (Firestore) | later 'ejar'
const COUNTER_PATH = ['settings', 'invoice_counter'];

/** Whether the shared counter issues the numbers (off today: the typed reference is used, as before). */
export const invoiceCounterOn = () => INVOICE_NUMBER_SOURCE === 'counter';

/**
 * The typed reference as the pages have always printed it (no database):
 *   one invoice                         prefix + typed                 (ALWFQ-100)
 *   several, mode 'sequence', digits    prefix + n, n+1, ...           (ALWFQ-100, ALWFQ-101)
 *   several, mode 'sequence', other     prefix + typed_1, typed_2 ...  (ALWFQ-A_1, ALWFQ-A_2)
 *   several, mode 'same'                prefix + typed on every one    (no page uses it: Parking once did)
 * Nothing typed: `blank` (a text, or a function giving one) after the prefix; blank null = required.
 */
export function typedInvoiceNumbers(typed, count = 1, { prefix = INVOICE_PREFIX, mode = 'sequence', blank = null } = {}) {
    const n = Math.max(1, Math.floor(Number(count) || 1));
    let t = String(typed ?? '').trim();
    if (!t) {
        if (blank === null || blank === undefined) throw new InvoiceNumberError('Please enter a Reference Number', 'required');
        t = typeof blank === 'function' ? String(blank()) : String(blank);
        return Array(n).fill(`${prefix}${t}`);
    }
    if (n === 1 || mode === 'same') return Array(n).fill(`${prefix}${t}`);
    if (/^\d+$/.test(t)) {
        const start = parseInt(t, 10);
        return Array.from({ length: n }, (_, i) => `${prefix}${start + i}`);
    }
    return Array.from({ length: n }, (_, i) => `${prefix}${t}_${i + 1}`);
}

/** A reference typed on the Monthly Calculator: digits get the ALWFQ- prefix, anything else stays as typed. */
export const formatTypedInvoiceNo = (text) => {
    const t = String(text ?? '').trim();
    return /^\d+$/.test(t) ? `${INVOICE_PREFIX}${t}` : t;
};

/**
 * The numbers of `count` invoices. opts: typed (what the user typed), prefix / mode / blank (the page's own
 * numbering while the counter is off), reissue / by (counter only).
 */
export async function issueInvoiceNumbers(count = 1, { typed = '', prefix, mode, blank, reissue = false, by = '' } = {}) {
    if (!invoiceCounterOn()) return typedInvoiceNumbers(typed, count, { prefix, mode, blank });
    return claimInvoiceNumbers(count, { start: String(typed ?? '').trim() || null, reissue, by });
}

/** 'ALWFQ-0042', 'alwfq-42', '42', 42 -> 42; anything else -> null. */
export function parseInvoiceNumber(value) {
    const m = /^\s*(?:ALWFQ-)?0*(\d+)\s*$/i.exec(String(value ?? ''));
    if (!m) return null;
    const n = parseInt(m[1], 10);
    return Number.isSafeInteger(n) && n > 0 ? n : null;
}

/** 42 -> 'ALWFQ-42'. */
export const formatInvoiceNumber = (n) => `${INVOICE_PREFIX}${n}`;

export class InvoiceNumberError extends Error {
    constructor(message, code) {
        super(message);
        this.name = 'InvoiceNumberError';
        this.code = code;
    }
}

/**
 * The numbers to issue given the counter's `last` (null = no counter yet): pure, so it is tested alone.
 * Returns {numbers, last} - `last` is the counter's new value (unchanged for a reissue).
 */
export function planInvoiceNumbers(last, count = 1, { start = null, reissue = false } = {}) {
    const want = Math.max(1, Math.floor(Number(count) || 1));
    const first = start === null || start === undefined || start === '' ? null : parseInvoiceNumber(start);
    if (start !== null && start !== undefined && start !== '' && first === null) {
        throw new InvoiceNumberError(`"${start}" is not an invoice number - type digits only (ALWFQ- is added).`, 'invalid');
    }
    const numbers = (from) => Array.from({ length: want }, (_, i) => formatInvoiceNumber(from + i));
    if (reissue) {
        if (first === null) throw new InvoiceNumberError('Type the number of the invoice to print again.', 'invalid');
        if (last === null || first + want - 1 > last) {
            throw new InvoiceNumberError(`${formatInvoiceNumber(first + want - 1)} has not been issued yet - untick "print again" to issue it.`, 'not-issued');
        }
        return { numbers: numbers(first), last };
    }
    if (first === null) {
        if (last === null) {
            throw new InvoiceNumberError('No invoice has been numbered here yet - type the first invoice number once; the rest follow on.', 'no-counter');
        }
        return { numbers: numbers(last + 1), last: last + want };
    }
    if (last !== null && first <= last) {
        throw new InvoiceNumberError(`${formatInvoiceNumber(first)} is already issued - the next free number is ${formatInvoiceNumber(last + 1)}.`, 'used');
    }
    return { numbers: numbers(first), last: first + want - 1 };
}

const counterRef = () => doc(db, ...COUNTER_PATH);
const lastOf = (snap) => {
    if (!snap || !snap.exists()) return null;
    const n = Number(snap.data()?.last);
    return Number.isSafeInteger(n) && n > 0 ? n : null;
};

const denied = (err) => (err && (err.code === 'permission-denied' || /insufficient permissions/i.test(err.message || ''))
    ? new InvoiceNumberError('Your role cannot issue invoice numbers (database permission). Ask an admin.', 'permission-denied')
    : err);

/** The engine's refusal {code, detail} (invalid | used | not-next | not-issued | no-counter) as an InvoiceNumberError. */
const storeRefusal = (e) => {
    const data = e?.response?.data;
    if (data && typeof data.code === 'string' && typeof data.detail === 'string') return new InvoiceNumberError(data.detail, data.code);
    return denied(officeError(e));
};

/**
 * The next number to suggest ('ALWFQ-n'); null while the counter is off (nothing is read), before its first
 * use, or when it cannot be read.
 */
export async function suggestNextInvoiceNumber() {
    if (!invoiceCounterOn()) return null;
    try {
        if (USE_CONTRACTS_V2) return (await finesService.invoiceNumberNext())?.next || null;
        const last = lastOf(await getDoc(counterRef()));
        return last === null ? null : formatInvoiceNumber(last + 1);
    } catch {
        return null;
    }
}

/**
 * The counter itself (used by issueInvoiceNumbers when INVOICE_NUMBER_SOURCE = 'counter'; pages never call it):
 * issues `count` numbers (['ALWFQ-101', 'ALWFQ-102', ...]) in one transaction.
 * opts.start   the first number typed by the user (must be above the last issued); omitted = next free
 * opts.reissue print again numbers already issued (checked, nothing written)
 * opts.by      who issued them (kept on the counter doc)
 */
export async function claimInvoiceNumbers(count = 1, { start = null, reissue = false, by = '' } = {}) {
    if (USE_CONTRACTS_V2) {
        const typed = start === null || start === undefined ? '' : String(start).trim();
        try {
            const answer = await finesService.invoiceNumberIssue({
                count: Math.max(1, Math.floor(Number(count) || 1)),
                start: typed || null,
                reissue: !!reissue,
                context: by ? { by: String(by) } : {},
            });
            return answer.numbers;
        } catch (err) {
            throw storeRefusal(err);
        }
    }
    try {
        return await runTransaction(db, async (tx) => {
            const ref = counterRef();
            const plan = planInvoiceNumbers(lastOf(await tx.get(ref)), count, { start, reissue });
            if (!reissue) {
                tx.set(ref, { last: plan.last, last_issued: plan.numbers[plan.numbers.length - 1], by: String(by || ''),
                    last_updated: new Date().toISOString() });
            }
            return plan.numbers;
        });
    } catch (err) {
        throw denied(err);
    }
}
